import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { tournamentsApi, circuitsApi, rulesetsApi, snapshotsApi } from '../services/api';
import { Tournament, TournamentCreate, Circuit, CircuitCreate, Ruleset, PriceSnapshot } from '../types';
import DateTimePicker from '../components/common/DateTimePicker';
import { useAuth } from '../context/AuthContext';

const TournamentManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'tournaments' | 'circuits'>('tournaments');
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tournamentsResponse, circuitsResponse] = await Promise.all([
        tournamentsApi.getAll(),
        circuitsApi.getAll()
      ]);
      
      setTournaments(tournamentsResponse.data);
      setCircuits(circuitsResponse.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTournament = async (tournamentData: TournamentCreate) => {
    try {
      const response = await tournamentsApi.create(tournamentData);
      setTournaments([response.data, ...tournaments]);
    } catch (error) {
      console.error('Failed to create tournament:', error);
      alert('Failed to create tournament. Please try again.');
    }
  };

  const handleCreateCircuit = async (circuitData: CircuitCreate) => {
    try {
      const response = await circuitsApi.create(circuitData);
      setCircuits([response.data, ...circuits]);
    } catch (error) {
      console.error('Failed to create circuit:', error);
      alert('Failed to create circuit. Please try again.');
    }
  };

  const handleDeleteTournament = async (tournamentId: string) => {
    if (!window.confirm('Are you sure you want to delete this tournament?')) {
      return;
    }

    try {
      await tournamentsApi.delete(tournamentId);
      setTournaments(tournaments.filter(t => t.id !== tournamentId));
    } catch (error) {
      console.error('Failed to delete tournament:', error);
      alert('Failed to delete tournament. Please try again.');
    }
  };

  const handleDeleteCircuit = async (circuitId: string) => {
    if (!window.confirm('Are you sure you want to delete this circuit?')) {
      return;
    }

    try {
      await circuitsApi.delete(circuitId);
      setCircuits(circuits.filter(c => c.id !== circuitId));
    } catch (error) {
      console.error('Failed to delete circuit:', error);
      alert('Failed to delete circuit. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Tournament Management</h1>
        
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('tournaments')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'tournaments'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Tournaments
            </button>
            <button
              onClick={() => setActiveTab('circuits')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'circuits'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Circuits
            </button>
          </nav>
        </div>

        {activeTab === 'tournaments' && (
          <TournamentsTab
            tournaments={tournaments}
            onCreate={handleCreateTournament}
            onDelete={handleDeleteTournament}
          />
        )}

        {activeTab === 'circuits' && (
          <CircuitsTab
            circuits={circuits}
            onCreate={handleCreateCircuit}
            onDelete={handleDeleteCircuit}
          />
        )}
      </div>
    </div>
  );
};

interface TournamentsTabProps {
  tournaments: Tournament[];
  onCreate: (tournament: TournamentCreate) => void;
  onDelete: (tournamentId: string) => void;
}

const TournamentsTab: React.FC<TournamentsTabProps> = ({ tournaments, onCreate, onDelete }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Tournaments</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create Tournament
        </button>
      </div>

      {showCreateForm && (
        <CreateTournamentForm
          onSubmit={onCreate}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tournaments.map((tournament) => (
          <div key={tournament.id} className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{tournament.name}</h3>
            <p className="text-gray-600 mb-4">{tournament.description}</p>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Date:</span>
                <span className="text-sm font-medium">
                  {new Date(tournament.date).toLocaleDateString()}
                </span>
              </div>
              
              {tournament.circuit && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Circuit:</span>
                  <span className="text-sm font-medium">{tournament.circuit.name}</span>
                </div>
              )}
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Status:</span>
                <span className={`text-sm font-medium ${tournament.is_active ? 'text-green-600' : 'text-red-600'}`}>
                  {tournament.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="flex space-x-2">
              <Link
                to={`/tournament/${tournament.id}/submissions`}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm text-center"
              >
                View Tournament
              </Link>
              <button
                onClick={() => onDelete(tournament.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {tournaments.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">No tournaments found.</p>
        </div>
      )}
    </div>
  );
};

interface CircuitsTabProps {
  circuits: Circuit[];
  onCreate: (circuit: CircuitCreate) => void;
  onDelete: (circuitId: string) => void;
}

const CircuitsTab: React.FC<CircuitsTabProps> = ({ circuits, onCreate, onDelete }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Circuits</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create Circuit
        </button>
      </div>

      {showCreateForm && (
        <CreateCircuitForm
          onSubmit={onCreate}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {circuits.map((circuit) => (
          <div key={circuit.id} className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{circuit.name}</h3>
            <p className="text-gray-600 mb-4">{circuit.description}</p>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Slug:</span>
                <span className="text-sm font-medium">{circuit.slug}</span>
              </div>
              
              {circuit.start_date && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Start:</span>
                  <span className="text-sm font-medium">
                    {new Date(circuit.start_date).toLocaleDateString()}
                  </span>
                </div>
              )}
              
              {circuit.end_date && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">End:</span>
                  <span className="text-sm font-medium">
                    {new Date(circuit.end_date).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => onDelete(circuit.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {circuits.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">No circuits found.</p>
        </div>
      )}
    </div>
  );
};

interface CreateTournamentFormProps {
  onSubmit: (tournament: TournamentCreate) => void;
  onCancel: () => void;
}

const CreateTournamentForm: React.FC<CreateTournamentFormProps> = ({ onSubmit, onCancel }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<TournamentCreate>({
    name: '',
    description: '',
    date: '',
    circuit_id: '',
    ruleset_id: '',
    snapshot_id: '',
    registration_open: '',
    registration_close: '',
  });

  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [rulesets, setRulesets] = useState<Ruleset[]>([]);
  const [snapshots, setSnapshots] = useState<PriceSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRuleset, setSelectedRuleset] = useState<Ruleset | null>(null);

  useEffect(() => {
    loadFormData();
  }, []);

  const loadFormData = async () => {
    try {
      setLoading(true);
      const [circuitsResponse, rulesetsResponse, snapshotsResponse] = await Promise.all([
        circuitsApi.getAll(),
        rulesetsApi.getAll(),
        snapshotsApi.getAll(),
      ]);
      setCircuits(circuitsResponse.data);
      setRulesets(rulesetsResponse.data);
      setSnapshots(snapshotsResponse.data);
    } catch (error) {
      console.error('Failed to load form data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRulesetChange = (rulesetId: string) => {
    setFormData({ ...formData, ruleset_id: rulesetId });
    const ruleset = rulesets.find(r => r.id === rulesetId);
    setSelectedRuleset(ruleset || null);
    
    // Clear snapshot if ruleset doesn't have budget
    if (!ruleset?.budget_limit) {
      setFormData(prev => ({ ...prev, snapshot_id: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.date || !formData.ruleset_id) {
      alert('Name, date, and ruleset are required');
      return;
    }
    
    // Only require snapshot if ruleset has budget
    if (selectedRuleset?.budget_limit && !formData.snapshot_id) {
      alert('Price snapshot is required for rulesets with budget limits');
      return;
    }
    
    // Clean up empty strings to null for optional fields
    const cleanedData = {
      ...formData,
      circuit_id: formData.circuit_id || undefined,
      snapshot_id: formData.snapshot_id || undefined,
    };
    
    onSubmit(cleanedData);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-center items-center h-32">
          <div className="text-lg text-gray-600">Loading form data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Create Tournament</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tournament Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Budget Commander #1"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Optional description of the tournament..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(user?.role === 'TO' || user?.role === 'ADMIN') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Circuit
              </label>
              <select
                value={formData.circuit_id}
                onChange={(e) => setFormData({ ...formData, circuit_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No circuit (optional)</option>
                {circuits.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ruleset *
            </label>
            <select
              value={formData.ruleset_id}
              onChange={(e) => handleRulesetChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select a ruleset</option>
              {rulesets.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} ({r.budget_limit 
                    ? `${r.budget_currency === 'EUR' ? '€' : r.budget_currency === 'USD' ? '$' : 'TIX'}${r.budget_limit}`
                    : 'No Budget'
                  })
                </option>
              ))}
            </select>
          </div>

          {selectedRuleset?.budget_limit ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price Snapshot *
              </label>
              <select
                value={formData.snapshot_id}
                onChange={(e) => setFormData({ ...formData, snapshot_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a snapshot</option>
                {snapshots.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.description || 'Untitled Snapshot'} ({new Date(s.snapshot_date).toLocaleDateString()})
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price Snapshot
              </label>
              <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-500">
                Not required for rulesets without budget limits
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DateTimePicker
            label="Tournament Date"
            value={formData.date}
            onChange={(value) => setFormData({ ...formData, date: value })}
            required={true}
            includeTime={true}
            placeholder="Select tournament date and time"
          />

          <DateTimePicker
            label="Registration Opens"
            value={formData.registration_open}
            onChange={(value) => setFormData({ ...formData, registration_open: value })}
            required={false}
            includeTime={true}
            placeholder="Optional: Registration start"
          />

          <DateTimePicker
            label="Registration Closes"
            value={formData.registration_close}
            onChange={(value) => setFormData({ ...formData, registration_close: value })}
            required={false}
            includeTime={true}
            placeholder="Optional: Registration end"
          />
        </div>

        <div className="flex space-x-4">
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Tournament
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

interface CreateCircuitFormProps {
  onSubmit: (circuit: CircuitCreate) => void;
  onCancel: () => void;
}

const CreateCircuitForm: React.FC<CreateCircuitFormProps> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<CircuitCreate>({
    name: '',
    description: '',
    slug: '',
    start_date: '',
    end_date: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.slug) {
      alert('Name and slug are required');
      return;
    }
    onSubmit(formData);
  };

  const handleSlugChange = (name: string) => {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    setFormData({ ...formData, name, slug });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Create Circuit</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Circuit Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleSlugChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Holland Budget Circuit"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Slug *
          </label>
          <input
            type="text"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., holland-budget-circuit"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Optional description of the circuit..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex space-x-4">
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Circuit
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default TournamentManagement;
